import { useCallback, useEffect, useState } from 'react';

// Tabs whose new work arrived in this browser, what kind, and when, so they're marked until
// they're opened or the week is up. 'new' is a resource just made (generation, or chat or Add
// Resource adding it): the Ready pill. 'changed' is chat changing a tab that was already there:
// the dot. Browser-local, so someone else's resource, or a copy of one, is never marked.
export const READY_TABS_STORAGE_KEY = 'readyResourceTabs';
export const READY_NEW = 'new';
export const READY_CHANGED = 'changed';
// Covers making something on a Friday and coming back after the weekend.
export const READY_TAB_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const isFresh = (entry, now) =>
  (entry?.kind === READY_NEW || entry?.kind === READY_CHANGED) &&
  typeof entry.at === 'number' &&
  now - entry.at < READY_TAB_TTL_MS;

// { packetId: { at: markedAtMs, kind } }, leaving out entries past their time.
export const readReadyTabs = (now = Date.now()) => {
  try {
    const stored = JSON.parse(localStorage.getItem(READY_TABS_STORAGE_KEY));
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.fromEntries(Object.entries(stored).filter(([, entry]) => isFresh(entry, now)));
  } catch {
    return {};
  }
};

const writeReadyTabs = (tabs) => {
  try {
    localStorage.setItem(READY_TABS_STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // Storage full or blocked: marks last for this visit only.
  }
};

// The marked tabs, markReady(ids, kind) and forgetReady(ids). Opening a tab clears it.
export const useReadyResourceTabs = (activePacketId) => {
  const [readyTabs, setReadyTabs] = useState(() => readReadyTabs());

  // Starts from storage, not this window's copy, so a window open on an older copy doesn't
  // overwrite another window's marks or clears.
  const update = useCallback(
    (change) =>
      setReadyTabs(() => {
        const next = change(readReadyTabs());
        writeReadyTabs(next);
        return next;
      }),
    []
  );

  // A change to a new resource that hasn't been opened yet keeps it new: opening it shows the
  // change anyway.
  const markReady = useCallback(
    (packetIds, kind = READY_NEW) =>
      update((tabs) => {
        const now = Date.now();
        for (const id of packetIds) {
          tabs[id] = { at: now, kind: tabs[id]?.kind === READY_NEW ? READY_NEW : kind };
        }
        return tabs;
      }),
    [update]
  );

  const forgetReady = useCallback(
    (packetIds) =>
      update((tabs) => {
        for (const id of packetIds) delete tabs[id];
        return tabs;
      }),
    [update]
  );

  useEffect(() => {
    if (activePacketId && readyKind(readyTabs, activePacketId)) forgetReady([activePacketId]);
  }, [activePacketId, readyTabs, forgetReady]);

  return [readyTabs, markReady, forgetReady];
};

// The mark's name in Mixpanel (`tab_mark`): 'ready' (the pill), 'dot', or 'none'.
export const tabMarkLabel = (kind) =>
  ({ [READY_NEW]: 'ready', [READY_CHANGED]: 'dot' })[kind] ?? 'none';

// How a tab is marked now ('new', 'changed' or null), from the record useReadyResourceTabs returns.
export const readyKind = (readyTabs, packetId) =>
  isFresh(readyTabs[packetId], Date.now()) ? readyTabs[packetId].kind : null;
